import React, { useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, Modal, Select, Table } from '@/shared/components';
import { TRANSFER_PLAN_SCOPE_VALUES } from '../schemas/transferRule.schema';
import type { TransferRuleFormValues } from '../schemas/transferRule.schema';
import type { TransferPlanScope } from '../types/transferRule.types';

const SCOPE_LABELS: Record<TransferPlanScope, string> = {
    dealer_own: 'Dealer own',
    national: 'National',
    foreign_group_own: 'Foreign group own',
};

const SCOPE_OPTIONS = TRANSFER_PLAN_SCOPE_VALUES.map((value) => ({ value, label: SCOPE_LABELS[value] }));

interface Props {
    control: Control<TransferRuleFormValues>;
    /** Retired-product view-only mode: hides Add/Remove and disables every row control. */
    disabled?: boolean;
}

interface RowDatum {
    id: string;
    index: number;
}

/**
 * The bespoke useFieldArray list-builder over the shared Table (BandSubEditor precedent,
 * but rendered as a Table rather than free divs so the forbid-list reads as tabular data).
 * A freshly-appended row starts with empty scope selects (cast past the strict Zod enum
 * type — see handleAdd), which fail transferRuleSchema's enum check until the admin picks
 * both scopes, so a half-built row cannot be submitted. Remove is destructive-confirmed via
 * the shared Modal even though nothing is persisted until Save, guarding against accidental
 * loss of an in-progress row.
 */
export const ForbiddenDirectionsEditor: React.FC<Props> = ({ control, disabled = false }) => {
    const { fields, append, remove } = useFieldArray({ control, name: 'forbidden_directions' });
    const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

    const handleAdd = () => {
        append({
            // UI-only empty sentinel; not a valid TransferPlanScope until the admin selects
            // one, so the cast is intentional — Zod's required enum check blocks submit.
            from_scope: '' as TransferPlanScope,
            to_scope: '' as TransferPlanScope,
        });
    };

    const confirmRemove = () => {
        if (pendingRemoveIndex === null) return;
        remove(pendingRemoveIndex);
        setPendingRemoveIndex(null);
    };

    const rows: RowDatum[] = fields.map((field, index) => ({ id: field.id, index }));

    const columns: ColumnDef<RowDatum>[] = [
        {
            id: 'from_scope',
            header: 'From scope',
            enableSorting: false,
            cell: ({ row }) => {
                const index = row.original.index;
                return (
                    <Controller
                        name={`forbidden_directions.${index}.from_scope`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id={`fd-${index}-from`}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                options={SCOPE_OPTIONS}
                                // Select derives its accessible name from placeholder, so the
                                // From/To scope selects must not share "Select scope" — give each
                                // a distinct name so screen readers can tell them apart.
                                placeholder="From scope"
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                );
            },
        },
        {
            id: 'to_scope',
            header: 'To scope',
            enableSorting: false,
            cell: ({ row }) => {
                const index = row.original.index;
                return (
                    <Controller
                        name={`forbidden_directions.${index}.to_scope`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id={`fd-${index}-to`}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                options={SCOPE_OPTIONS}
                                // Distinct from the "From scope" select above (see note there):
                                // placeholder is the combobox's accessible name.
                                placeholder="To scope"
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                );
            },
        },
    ];

    // View-only mode (retired product) has no per-row Remove button, so omit the Actions
    // column entirely rather than render an empty header over null cells. The column is only
    // present when the editable affordance it hosts would actually render.
    if (!disabled) {
        columns.push({
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: ({ row }) => {
                const index = row.original.index;
                return (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        ariaLabel={`Remove forbidden direction ${index + 1}`}
                        onClick={() => setPendingRemoveIndex(index)}
                    >
                        Remove
                    </Button>
                );
            },
        });
    }

    return (
        <div className="space-y-3">
            <Table<RowDatum>
                data={rows}
                columns={columns}
                enableSorting={false}
                emptyMessage="No forbidden directions configured."
            />

            {!disabled && (
                <Button type="button" size="sm" variant="outline" onClick={handleAdd}>
                    Add forbidden direction
                </Button>
            )}

            <Modal
                isOpen={pendingRemoveIndex !== null}
                onClose={() => setPendingRemoveIndex(null)}
                ariaLabelledBy="remove-forbidden-direction-heading"
                className="max-w-md p-6"
            >
                <h2 id="remove-forbidden-direction-heading" className="text-lg font-semibold mb-2">
                    Remove forbidden direction
                </h2>
                <p className="text-sm text-mp-muted mb-4">
                    Remove this forbidden direction? This cannot be undone.
                </p>
                <div className="flex justify-end gap-2">
                    <Button type="button" onClick={() => setPendingRemoveIndex(null)} variant="clear">
                        Cancel
                    </Button>
                    <Button type="button" onClick={confirmRemove} variant="danger">
                        Remove
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
